import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, ClickAwayListener, SxProps, Theme, Tooltip, Typography } from '@mui/material';

const PERCENTAGE_BAR_CHART_STRINGS = appStrings.modules.charts.percentageBarChart;

type Marker = Readonly<{
  label: string;
  value: number;
}>;

export type PercentageBarChartProps = Readonly<{
  value: number;
  title: string;
  tooltipText: string;
  topMarker?: Marker;
  bottomMarker?: Marker;
}>;

export const PercentageBarChart = ({
  value,
  title,
  tooltipText,
  topMarker,
  bottomMarker,
}: PercentageBarChartProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const displayValue = Math.round(value * 100);
  const topMarkerDisplay = topMarker ? Math.round(topMarker.value * 100) : undefined;
  const bottomMarkerDisplay = bottomMarker ? Math.round(bottomMarker.value * 100) : undefined;

  return (
    <Box sx={containerSx}>
      {/* HEADER */}
      <Box sx={headerSx}>
        <Typography variant="text-sm-semibold">{title}</Typography>

        <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
          <Tooltip
            open={isTooltipOpen}
            onClose={() => setIsTooltipOpen(false)}
            title={tooltipText}
            placement="top"
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener
            slotProps={{
              tooltip: {
                sx: ({ backrs }) => ({
                  whiteSpace: 'pre-line',
                  padding: backrs.spacing['spacing-xl'],
                }),
              },
            }}
          >
            <Box
              id={`percentage-bar-info-${title.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setIsTooltipOpen((prev) => !prev)}
              sx={iconContainerSx}
            >
              <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
            </Box>
          </Tooltip>
        </ClickAwayListener>
      </Box>

      {/* CHART CONTENT */}
      <Box sx={contentSx}>
        <Typography variant="display-sm-semibold" sx={valueSx}>
          {displayValue}%
        </Typography>
        {/* TOP MARKERS CONTAINER */}
        <Box sx={markersAboveBarSx}>
          {/* 0% marker */}
          <Box sx={{ ...tickMarkerPositionSx, left: '0%' }}>
            <Typography variant="text-xs-regular" sx={markerLabelSx}>
              {PERCENTAGE_BAR_CHART_STRINGS.minLabel}
            </Typography>
          </Box>

          {/* Top marker */}
          {topMarker && topMarkerDisplay !== undefined && (
            <Box sx={{ ...markerPositionSx, left: `${topMarkerDisplay}%` }}>
              <Typography variant="text-xs-regular" sx={markerLabelSx}>
                {topMarker.label}: {topMarkerDisplay}%
              </Typography>
              <Box sx={triangleDownSx} />
            </Box>
          )}

          {/* 100% marker */}
          <Box sx={{ ...tickMarkerPositionSx, left: '100%' }}>
            <Typography variant="text-xs-regular" sx={markerLabelSx}>
              {PERCENTAGE_BAR_CHART_STRINGS.maxLabel}
            </Typography>
          </Box>
        </Box>

        {/* PROGRESS BAR */}
        <Box sx={barContainerSx}>
          <Box sx={getBarFillSx(`${displayValue}%`)} />

          {/* Top marker vertical line */}
          {topMarker && topMarkerDisplay !== undefined && (
            <Box sx={getMarkerLineSx(topMarkerDisplay)} />
          )}

          {/* Bottom marker vertical line */}
          {bottomMarker && bottomMarkerDisplay !== undefined && (
            <Box sx={getMarkerLineSx(bottomMarkerDisplay)} />
          )}
        </Box>

        {/* BOTTOM MARKER */}
        {bottomMarker && bottomMarkerDisplay !== undefined && (
          <Box sx={markerContainerBelowSx}>
            <Box sx={{ ...markerPositionSx, left: `${bottomMarkerDisplay}%` }}>
              <Box sx={triangleUpSx} />
              <Typography variant="text-xs-regular" sx={markerLabelSx}>
                {bottomMarker.label}: {bottomMarkerDisplay}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingX: backrs.spacing['spacing-2xl'],
  paddingY: backrs.spacing['spacing-lg'],
});

const iconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const valueSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-lg'],
});

const markersAboveBarSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  height: backrs.spacing['spacing-4xl'],
  marginBottom: backrs.spacing['spacing-xs'],
});

const barContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: backrs.spacing['spacing-3xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
  position: 'relative',
});

const getBarFillSx =
  (width: string): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '100%',
    width,
    backgroundColor: COLOR_TOKENS['Brand/500'],
    borderTopRightRadius: backrs.radius['radius-sm'],
    borderBottomRightRadius: backrs.radius['radius-sm'],
    position: 'relative',
  });

const markerContainerBelowSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  height: backrs.spacing['spacing-4xl'],
  marginTop: backrs.spacing['spacing-xs'],
});

const markerPositionSx: SxProps<Theme> = {
  position: 'absolute',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const tickMarkerPositionSx: SxProps<Theme> = {
  position: 'absolute',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  bottom: 0,
};

const markerLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'nowrap',
});

const triangleDownSx: SxProps<Theme> = ({ backrs }) => ({
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: `6px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  marginTop: backrs.spacing['spacing-xxs'],
});

const triangleUpSx: SxProps<Theme> = ({ backrs }) => ({
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderBottom: `6px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  marginBottom: backrs.spacing['spacing-xxs'],
});

const getMarkerLineSx = (positionPercent: number): SxProps<Theme> => ({
  position: 'absolute',
  top: 0,
  left: `${positionPercent}%`,
  height: '100%',
  width: '12px',
  backgroundColor: COLOR_TOKENS['Colors/Base/Black'],
  transform: 'translateX(calc(-50% + 1px))',
  zIndex: 10,
  pointerEvents: 'none',
});
